import { Box, Card, CardContent, Divider, FormControl, FormHelperText, Typography } from '@mui/material';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { OptionsObject, SnackbarProvider } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import { ReactConfirmProps, confirmable, createConfirmation } from 'react-confirm';
import { WithContext as ReactTags } from 'react-tag-input';
import { WithChildrenProps } from '../';
import CommentTaskType from '../../enum/CommentTaskType';
import FixedIds from '../../enum/FixedIds';
import Tag from '../../model/Tag';
import User from '../../model/User';
import { ServiceApi } from '../../service/ServiceApiWithLoading';
import TaskApi from '../../service/TaskApi';
import { ThemeBlue, ThemeBlueDark } from "../../themes/ThemeBlue";
import DefaultButton from '../FormControl/DefaultButton';
import { FormDialogNoTheme } from '../FormControl/FormDialog';
import ErrorButton from './../FormControl/ErrorButton';
import TagType from '../../enum/TagType';
import SearchEntity from '../SelectSearch/SearchEntity';
import TicketRootCause from '../../model/TicketRootCause';

interface ConfirmationOptions {
    onProceed: (values: TaskAddTagDialogFormData | null) => Promise<any> | any;
    onCancel?: () => any;
    user_name?: string;
    task_id: number;
    enqueueSnackbar: (message: React.ReactNode, options?: OptionsObject) => void
}

interface TaskAddTagDialogProps extends WithChildrenProps {
    options: ConfirmationOptions,
    ProceedButton?: React.ComponentType<any>
}

interface TaskAddTagDialogFormData {
    tags: ReactTagsData[];
    ticket_root_cause_id: number | null;
}

export interface ReactTagsData {
    id: string;
    text: string;
}

const KeyCodes = {
    comma: 188,
    enter: 13
};
const delimiters = [KeyCodes.comma, KeyCodes.enter];

const TaskAddTagDialog: React.FC<ReactConfirmProps & TaskAddTagDialogProps> = (props) => {
    const { show, dismiss, options: { onCancel, onProceed, user_name, task_id, enqueueSnackbar }, proceed } = props;
    
    const [tagSuggestions, setTagSuggestions] = useState<ReactTagsData[]>([]);
    const [requireTag, setRequireTag] = useState<boolean>(false);

    const theme = localStorage.getItem("theme");
    const taskApi = React.useRef(new TaskApi()).current;
    const apiUser = React.useRef(new ServiceApi<User>('user')).current;
    const apiTag = React.useRef(new ServiceApi<Tag>('tag')).current;

    // Carregar sugestões
    useEffect(() => {
        let mounted = true;
        apiTag.getList({ order: { name: 'ASC' }, where: [['type', '=', TagType.Task]] }).then(suggestions => {
            if (mounted) {
                setTagSuggestions(suggestions.map((tag) => ({ id: String(tag.id), text: tag.name })));
            }
        });
        return () => { mounted = false };
    }, [apiTag]);

    // Adicionar tag
    const onAddTag = useCallback(async (t: ReactTagsData, tags: ReactTagsData[], setFieldValue: any) => {
        let tag = { ...t };
        const searchTag = await apiTag.getList({ where: [['name', '=', t.text], ['type', '=', TagType.Task]] });
        if (searchTag.length > 0) {
            tag.id = String(searchTag[0].id);
            tag.text = searchTag[0].name;
        } else {
            const newTag = await apiTag.insert({ name: t.text, type: TagType.Task });
            tag.id = String(newTag.id);
        }
        if(tags.find(t => t.text.toLowerCase() === tag.text.toLowerCase())) {
            return;
        }
        await setFieldValue('tags', [...tags, tag]);
    }, [apiTag]);

    // Enviar
    const onSubmit = useCallback(async ({ tags, ticket_root_cause_id }: TaskAddTagDialogFormData) => {
        if(requireTag && tags.length === 0) {
            enqueueSnackbar('É necessário adicionar uma TAG.', { variant: 'error' });
            return;
        }
        
        const lanzaBotList = await apiUser.getList({ where: [['name', '=', 'LanzaBot']] });
        
        if (requireTag && tags.length > 0) {
            // Quando há tag, enviar comentário com a versão
            const saveComment = await taskApi.saveComment(
                task_id,
                `Pedro, ${user_name} indicou que este chamado originou um desenvolvimento na versão ${tags[0].text}.`,
                false,
                lanzaBotList[0].id,
                CommentTaskType.PreService,
                undefined,
                undefined,
                [FixedIds.idPedro]
            );
            if (saveComment) {
                if ((await Promise.resolve(onProceed({ tags, ticket_root_cause_id }))) === false) {
                    return;
                }
                if (typeof proceed === 'function') {
                    proceed('ok');
                }
            }
        } else {
            // Quando não há tag (respondeu "não"), apenas proceder
            if ((await Promise.resolve(onProceed(null))) === false) {
                return;
            }
            if (typeof proceed === 'function') {
                proceed('ok');
            }
        }
    }, [requireTag, apiUser, taskApi, task_id, user_name, onProceed, proceed]);

    // Validação (mantido igual)
    const getValidationSchema = useCallback(() => {
        const validationSchema: any = {};
        return validationSchema;
    }, []);

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme === 'dark' ? ThemeBlueDark : ThemeBlue}>
                <SnackbarProvider maxSnack={3} autoHideDuration={2000} disableWindowBlurListener>
                    <FormDialogNoTheme<TaskAddTagDialogFormData>
                        show={show}
                        title={
                            <Box sx={{ textAlign: 'center', py: 1 }}>
                                <Typography variant="h5" component="div" sx={{ 
                                    fontWeight: 600, 
                                    color: 'primary.main',
                                    mb: 1 
                                }}>
                                    🔧 Alteração de Software/Firmware
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                    color: 'text.secondary',
                                    fontStyle: 'italic'
                                }}>
                                    Foi feita alguma alteração de software ou firmware?
                                </Typography>
                            </Box>
                        }
                        dismiss={() => dismiss()}
                        onSubmit={(values, dirty, errors) => {
                            if (errors && Object.keys(errors).length > 0) {
                                return;
                            }
                            onSubmit(values);
                        }}
                        initialValues={{ tags: [], ticket_root_cause_id: null }}
                        enableReinitialize={true}
                        maxWidth='sm'
                        fullWidth
                        validationSchema={getValidationSchema()}
                    >
                        {({ values, errors, setFieldValue, isSubmitting }) => (
                            <>
                                {!requireTag ? (
                                    // Primeira etapa: pergunta inicial com design moderno
                                    <Box sx={{ textAlign: 'center', py: 3 }}>
                                        <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                                            <ErrorButton
                                                size='large'
                                                variant='contained'
                                                disabled={isSubmitting}
                                                type='submit'
                                                sx={{ 
                                                    minWidth: 120,
                                                    py: 1.5,
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    fontSize: '1rem',
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: 2
                                                    }
                                                }}>
                                                Não
                                            </ErrorButton>
                                            <DefaultButton
                                                size='large'
                                                variant="contained"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setRequireTag(true);
                                                }}
                                                disabled={isSubmitting}
                                                type='button'
                                                sx={{ 
                                                    minWidth: 120,
                                                    py: 1.5,
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    fontSize: '1rem',
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: 3
                                                    }
                                                }}>
                                                Sim
                                            </DefaultButton>
                                        </Box>
                                    </Box>
                                ) : (
                                    // Segunda etapa: input de tag com design moderno
                                    <Box sx={{ py: 2 }}>
                                        <Card variant="outlined" sx={{ mb: 3, borderRadius: 2, backgroundColor: 'transparent', overflow: 'unset' }}>
                                            <CardContent sx={{ py: 2 }}>
                                                <Typography variant="h6" sx={{ 
                                                    mb: 2, 
                                                    color: 'primary.main',
                                                    fontWeight: 600,
                                                    textAlign: 'center'
                                                }}>
                                                    Em qual versão?
                                                </Typography>
                                                <Divider sx={{ mb: 2 }} />
                                                <FormControl fullWidth variant="outlined">
                                                    <ReactTags
                                                        tags={values.tags}
                                                        suggestions={tagSuggestions}
                                                        delimiters={delimiters}
                                                        handleDelete={(i) => {
                                                            setFieldValue('tags', values.tags.filter((_, index) => index !== i));
                                                        }}
                                                        handleAddition={async (t) => {
                                                            if (t.text.length > 2) {
                                                                await onAddTag(t, values.tags, setFieldValue);
                                                            } else {
                                                                enqueueSnackbar('Mínimo de 3 caracteres para adicionar uma TAG.', { variant: 'error' });
                                                            }
                                                        }}
                                                        handleTagClick={(index) => {
                                                            console.log('The tag at index ' + index + ' was clicked');
                                                        }}
                                                        allowDragDrop={false}
                                                        placeholder="Digite a TAG"
                                                        classNames={{
                                                            tagInputField: theme === 'dark' ? 'ReactTags__tagInputFieldDark' : 'ReactTags__tagInputField'
                                                        }}
                                                        allowAdditionFromPaste={false}
                                                        minQueryLength={1}
                                                        allowDeleteFromEmptyInput={false}
                                                    />
                                                    <FormHelperText sx={{ mt: 1, fontSize: '0.85rem' }}>
                                                        💡 Digite o nome da versão e pressione ENTER para adicionar
                                                    </FormHelperText>
                                                </FormControl>
                                            </CardContent>
                                        </Card>
                                        <Card variant="outlined" sx={{ mb: 3, borderRadius: 2, backgroundColor: 'transparent', overflow: 'unset' }}>
                                            <CardContent sx={{ py: 2 }}>
                                                <Typography variant="h6" sx={{ 
                                                    mb: 2, 
                                                    color: 'primary.main',
                                                    fontWeight: 600,
                                                    textAlign: 'center'
                                                }}>
                                                    Qual a causa raiz?
                                                </Typography>
                                                <Divider sx={{ mb: 2 }} />
                                                <FormControl fullWidth variant="outlined">
                                                    <SearchEntity<TicketRootCause, 'id', 'name'>
                                                        name="ticket_root_cause_id"
                                                        entity="ticketRootCause"
                                                        valueField='id'
                                                        labelField="name"
                                                        label="Causa Raiz"
                                                        value={values.ticket_root_cause_id || null}
                                                        onChangeHandle={(_, v) => setFieldValue('ticket_root_cause_id', v)}
                                                        extraWhere={[['use_in_task', '=', true]]}
                                                        isClearable
                                                    />
                                                    <FormHelperText sx={{ mt: 1, fontSize: '0.85rem' }}>
                                                        💡 Selecione a causa raiz relacionada a este desenvolvimento (opcional)
                                                    </FormHelperText>
                                                </FormControl>
                                            </CardContent>
                                        </Card>
                                        
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                                            <ErrorButton
                                                size='medium'
                                                variant='contained'
                                                onClick={() => {
                                                    if (typeof onCancel === 'function') {
                                                        onCancel();
                                                    } else {
                                                        dismiss();
                                                    }
                                                }}
                                                sx={{
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    minWidth: 100
                                                }}>
                                                Cancelar
                                            </ErrorButton>
                                            <DefaultButton
                                                size='medium'
                                                type='submit'
                                                variant="contained"
                                                disabled={isSubmitting}
                                                sx={{
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    minWidth: 100,
                                                    '&:hover': {
                                                        boxShadow: 2
                                                    }
                                                }}>
                                                Confirmar
                                            </DefaultButton>
                                        </Box>
                                    </Box>
                                )}
                            </>
                        )}
                    </FormDialogNoTheme>
                </SnackbarProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

// Mesma lógica confirmable + createConfirmation
const ConfirmableDialog = confirmable<ReactConfirmProps & TaskAddTagDialogProps>(TaskAddTagDialog);


const confirm = createConfirmation(ConfirmableDialog, 400, document.querySelector('.App'));

export default function taskAddTagDialog(options: ConfirmationOptions) {
    return confirm({ options });
}
